import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { toast } from 'sonner';

interface ImagePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string | null;
  isVideo?: boolean;
}

export const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  isOpen,
  onClose,
  imageUrl,
  isVideo = false
}) => {
  if (!imageUrl) return null;

  const handleDownload = () => {
    try {
      const filename = `preview-${Date.now()}.${isVideo ? 'mp4' : 'png'}`;
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Download started');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Download failed. Please try right-clicking and saving.');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle>{isVideo ? 'Video' : 'Image'} Preview</DialogTitle>
        </DialogHeader>
        
        <div className="px-6 pb-6 relative">
          <div className="w-full max-h-[70vh] flex items-center justify-center bg-black/5 rounded-lg overflow-hidden">
            {isVideo ? (
              <video
                src={imageUrl}
                className="max-w-full max-h-full object-contain"
                controls
                autoPlay
                muted
                loop
              />
            ) : (
              <img
                src={imageUrl}
                alt="Preview"
                className="max-w-full max-h-full object-contain"
                style={{ maxHeight: '70vh' }}
              />
            )}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            className="absolute bottom-10 right-10 flex items-center gap-2 shadow-lg"
          >
            <Download className="h-4 w-4" />
            Download
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};